import { makeAutoObservable, runInAction } from "mobx"
import { connectNestClient, nestClient } from "../../nest/client"
import type { FollowupSchedule, FollowupSessionSummary } from "../../nest/generated"
import type { TaskRecord } from "../../persistence/records"
import { requestWildLarkMethod } from "../../wildlark/client"
import type { CodeStore } from "../store"

const TERMINAL_STATES = new Set<FollowupSchedule["state"]>(["completed", "cancelled"])
const SNAPSHOT_SESSION_BATCH_SIZE = 500
const SNAPSHOT_CONCURRENCY = 4

function taskSessionIds(task: TaskRecord | undefined): string[] {
    if (!task) return []
    const sessionIds = new Set<string>()
    for (const session of task.sessions) {
        if (session.nestSessionId) sessionIds.add(session.nestSessionId)
    }
    return [...sessionIds]
}

export interface ScheduledTaskSummary {
    count: number
    nextAt?: string
    hasAttention: boolean
}

interface ScheduledSessionBatch {
    workspaceId: string
    sessionIds: string[]
}

export class ScheduledTurnManager {
    private schedulesById = new Map<string, FollowupSchedule>()
    private scheduleIdsBySession = new Map<string, Set<string>>()
    private summariesBySession = new Map<string, FollowupSessionSummary>()
    private reconcileQueue: Promise<void> = Promise.resolve()
    private refreshPromise: Promise<void> | null = null
    private updatesDuringRefresh: Map<string, FollowupSchedule> | null = null
    private subscriptionDisposer: (() => void) | null = null
    private statusDisposer: (() => void) | null = null
    private connectionGeneration = 0

    constructor(private store: CodeStore) {
        makeAutoObservable<
            ScheduledTurnManager,
            "store" | "reconcileQueue" | "refreshPromise" | "updatesDuringRefresh" | "subscriptionDisposer" | "statusDisposer" | "connectionGeneration"
        >(this, {
            store: false,
            reconcileQueue: false,
            refreshPromise: false,
            updatesDuringRefresh: false,
            subscriptionDisposer: false,
            statusDisposer: false,
            connectionGeneration: false,
        })
    }

    async initialize(): Promise<void> {
        await this.connect().catch((error) => {
            console.warn("[ScheduledTurnManager] Failed to load scheduled follow-ups; live notifications may be unavailable", error)
        })
    }

    async connect(): Promise<void> {
        this.disconnect()
        const unsubscribe = nestClient.on("schedule.updated", (params) => {
            if (params) this.applyUpdate(params.schedule)
        })
        let releaseSubscription: (() => void) | null = null
        try {
            const client = await connectNestClient()
            releaseSubscription = await client.retainSubscriptions([{ method: "schedule.updated" }])
        } catch (error) {
            unsubscribe()
            throw error
        }
        this.subscriptionDisposer = () => {
            unsubscribe()
            releaseSubscription?.()
        }
        this.statusDisposer = nestClient.onStatusChange((status) => {
            if (status !== "connected") return
            void this.refresh().catch((error) => {
                console.warn("[ScheduledTurnManager] Failed to refresh scheduled follow-ups after reconnect", error)
            })
        })
        await this.refresh()
    }

    async refresh(): Promise<void> {
        if (this.refreshPromise) return this.refreshPromise
        const bufferedUpdates = new Map<string, FollowupSchedule>()
        this.updatesDuringRefresh = bufferedUpdates
        const generation = this.connectionGeneration
        const refresh = this.replaceFromSnapshot(bufferedUpdates, generation).finally(() => {
            if (this.updatesDuringRefresh === bufferedUpdates) this.updatesDuringRefresh = null
            if (this.refreshPromise === refresh) this.refreshPromise = null
        })
        this.refreshPromise = refresh
        return refresh
    }

    private async replaceFromSnapshot(bufferedUpdates: Map<string, FollowupSchedule>, generation: number): Promise<void> {
        const schedules = new Map<string, FollowupSchedule>()
        const summaries = new Map<string, FollowupSessionSummary>()
        const batches = this.sessionBatches()
        let nextBatch = 0
        const workerCount = Math.min(SNAPSHOT_CONCURRENCY, batches.length)
        await Promise.all(
            Array.from({ length: workerCount }, async () => {
                while (nextBatch < batches.length) {
                    const batch = batches[nextBatch]
                    nextBatch += 1
                    await this.loadSnapshotBatch(batch, schedules, summaries, generation)
                }
            })
        )
        if (generation !== this.connectionGeneration) return
        runInAction(() => {
            this.schedulesById.clear()
            this.scheduleIdsBySession.clear()
            for (const schedule of schedules.values()) this.storeSchedule(schedule)
            this.summariesBySession = summaries
            for (const schedule of bufferedUpdates.values()) this.applyScheduleState(schedule)
        })
        for (const summary of summaries.values()) this.enqueueSummaryReconciliation(summary)
    }

    private async loadSnapshotBatch(
        batch: ScheduledSessionBatch,
        schedules: Map<string, FollowupSchedule>,
        summaries: Map<string, FollowupSessionSummary>,
        generation: number
    ): Promise<void> {
        let cursor: string | undefined
        for (;;) {
            if (generation !== this.connectionGeneration) return
            const page = await requestWildLarkMethod("wildlark.followup.list", {
                workspaceId: batch.workspaceId,
                sessionIds: batch.sessionIds,
                limit: 500,
                ...(cursor ? { cursor } : {}),
            })
            for (const schedule of page.schedules) schedules.set(schedule.scheduleId, schedule)
            for (const summary of page.sessionSummaries) {
                const previous = summaries.get(summary.sessionId)
                if (!previous || previous.lastStartedAt < summary.lastStartedAt) {
                    summaries.set(summary.sessionId, summary)
                }
            }
            if (!page.hasMore) return
            if (!page.nextCursor) throw new Error("Nest omitted the next scheduled-follow-up cursor")
            cursor = page.nextCursor
        }
    }

    applyUpdate(schedule: FollowupSchedule): void {
        this.updatesDuringRefresh?.set(schedule.scheduleId, schedule)
        let summary: FollowupSessionSummary | undefined
        runInAction(() => {
            summary = this.applyScheduleState(schedule)
        })
        if (summary) this.enqueueSummaryReconciliation(summary)
    }

    forTask(taskId: string): FollowupSchedule[] {
        const task = this.store.productState?.tasks.get(taskId)
        const schedules: FollowupSchedule[] = []
        for (const sessionId of taskSessionIds(task)) {
            for (const scheduleId of this.scheduleIdsBySession.get(sessionId) ?? []) {
                const schedule = this.schedulesById.get(scheduleId)
                if (schedule && schedule.state !== "started") schedules.push(schedule)
            }
        }
        return schedules.sort((left, right) => left.at.localeCompare(right.at) || left.scheduleId.localeCompare(right.scheduleId))
    }

    summaryForTask(taskId: string): ScheduledTaskSummary {
        const task = this.store.productState?.tasks.get(taskId)
        let count = 0
        let nextAt: string | undefined
        let hasAttention = false
        for (const sessionId of taskSessionIds(task)) {
            for (const scheduleId of this.scheduleIdsBySession.get(sessionId) ?? []) {
                const schedule = this.schedulesById.get(scheduleId)
                if (!schedule || schedule.state === "started") continue
                count += 1
                if (nextAt === undefined || schedule.at < nextAt) nextAt = schedule.at
                if (schedule.state === "failed" || schedule.state === "parked") hasAttention = true
            }
        }
        return {
            count,
            nextAt,
            hasAttention,
        }
    }

    async cancel(schedule: FollowupSchedule): Promise<void> {
        const taskId = this.store.findTaskIdForNestSession(schedule.sessionId)
        if (!taskId) throw new Error("The task for this scheduled follow-up is no longer available")
        const result = await requestWildLarkMethod("wildlark.followup.cancel", {
            taskId,
            workspaceId: schedule.workspaceId,
            sessionId: schedule.sessionId,
            scheduleId: schedule.scheduleId,
            expectedVersion: schedule.version,
        })
        if (!result.cancelled) {
            await this.refresh()
            throw new Error("The scheduled follow-up already changed or started")
        }
        runInAction(() => this.removeSchedule(schedule.scheduleId))
    }

    async runNow(schedule: FollowupSchedule): Promise<void> {
        const taskId = this.store.findTaskIdForNestSession(schedule.sessionId)
        if (!taskId) throw new Error("The task for this scheduled follow-up is no longer available")
        const result = await requestWildLarkMethod("wildlark.followup.runNow", {
            taskId,
            workspaceId: schedule.workspaceId,
            sessionId: schedule.sessionId,
            scheduleId: schedule.scheduleId,
            expectedVersion: schedule.version,
        })
        this.applyUpdate(result.schedule)
    }

    forgetTask(task: TaskRecord): void {
        runInAction(() => {
            for (const sessionId of taskSessionIds(task)) {
                for (const scheduleId of this.scheduleIdsBySession.get(sessionId) ?? []) this.removeSchedule(scheduleId)
                this.summariesBySession.delete(sessionId)
            }
        })
    }

    reconcileTask(taskId: string): void {
        const task = this.store.productState?.tasks.get(taskId)
        for (const sessionId of taskSessionIds(task)) {
            const summary = this.summariesBySession.get(sessionId)
            if (summary) this.enqueueSummaryReconciliation(summary)
        }
    }

    disconnect(): void {
        this.connectionGeneration += 1
        this.subscriptionDisposer?.()
        this.subscriptionDisposer = null
        this.statusDisposer?.()
        this.statusDisposer = null
        this.refreshPromise = null
        this.updatesDuringRefresh = null
        this.schedulesById.clear()
        this.scheduleIdsBySession.clear()
        this.summariesBySession.clear()
    }

    private applyScheduleState(schedule: FollowupSchedule): FollowupSessionSummary | undefined {
        this.removeSchedule(schedule.scheduleId)
        if (!TERMINAL_STATES.has(schedule.state)) this.storeSchedule(schedule)
        if (!schedule.startedAt) return
        const summary: FollowupSessionSummary = {
            sessionId: schedule.sessionId,
            workspaceId: schedule.workspaceId,
            lastStartedAt: schedule.startedAt,
            lastScheduleId: schedule.scheduleId,
        }
        const previous = this.summariesBySession.get(schedule.sessionId)
        if (!previous || previous.lastStartedAt < summary.lastStartedAt) {
            this.summariesBySession.set(schedule.sessionId, summary)
            return summary
        }
        return undefined
    }

    private storeSchedule(schedule: FollowupSchedule): void {
        this.schedulesById.set(schedule.scheduleId, schedule)
        const ids = this.scheduleIdsBySession.get(schedule.sessionId) ?? new Set<string>()
        ids.add(schedule.scheduleId)
        this.scheduleIdsBySession.set(schedule.sessionId, ids)
    }

    private removeSchedule(scheduleId: string): void {
        const previous = this.schedulesById.get(scheduleId)
        if (!previous) return
        this.schedulesById.delete(scheduleId)
        const ids = this.scheduleIdsBySession.get(previous.sessionId)
        ids?.delete(scheduleId)
        if (ids?.size === 0) this.scheduleIdsBySession.delete(previous.sessionId)
    }

    private sessionBatches(): ScheduledSessionBatch[] {
        const byWorkspace = new Map<string, Set<string>>()
        for (const task of this.store.productState?.tasks.values() ?? []) {
            const ids = byWorkspace.get(task.workspaceId) ?? new Set<string>()
            for (const sessionId of taskSessionIds(task)) ids.add(sessionId)
            if (ids.size > 0) byWorkspace.set(task.workspaceId, ids)
        }
        const batches: ScheduledSessionBatch[] = []
        for (const [workspaceId, ids] of [...byWorkspace].sort(([left], [right]) => left.localeCompare(right))) {
            const sessionIds = [...ids].sort()
            for (let index = 0; index < sessionIds.length; index += SNAPSHOT_SESSION_BATCH_SIZE) {
                batches.push({ workspaceId, sessionIds: sessionIds.slice(index, index + SNAPSHOT_SESSION_BATCH_SIZE) })
            }
        }
        return batches
    }

    private enqueueSummaryReconciliation(summary: FollowupSessionSummary): void {
        const generation = this.connectionGeneration
        this.reconcileQueue = this.reconcileQueue
            .then(async () => {
                if (generation !== this.connectionGeneration) return
                const taskId = this.store.findTaskIdForNestSession(summary.sessionId)
                if (!taskId) return
                await this.store.tasks.reopenForScheduledStart(taskId, summary.sessionId, summary.lastScheduleId, summary.lastStartedAt)
            })
            .catch((error) => {
                console.warn("[ScheduledTurnManager] Failed to reconcile scheduled task start", {
                    sessionId: summary.sessionId,
                    error,
                })
            })
    }
}
